import React, { useState, useContext, useEffect } from 'react';
import apiClient from '../api/apiClient.js';
import { Box, TextField, Button, Typography, Snackbar, MenuItem, Select, FormControl, InputLabel, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import CameraScanner from '../components/CameraScanner';
import { OrgContext } from '../contexts/OrgContext'; // Import OrgContext
import { CurrencyContext } from '../contexts/CurrencyContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useLoading } from '../contexts/LoadingContext';
import BackButton from '../components/BackButton';  // Add this import
import CustomDatePicker from '../components/CustomDatePicker';

function AddProductPage() {
  const [productName, setProductName] = useState('');
  const [category, setCategory] = useState('');
  const [unitOfMeasure, setUnitOfMeasure] = useState('');
  const [scannerRef, setScannerRef] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [typeOfInventory, setTypeOfInventory] = useState('permanent'); // Add typeOfInventory state
  const [company, setCompany] = useState('');
  const [sku, setSku] = useState('');
  const [currentUnitCost, setCurrentUnitCost] = useState('');
  const [currentUnitPrice, setCurrentUnitPrice] = useState('');
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarErrorOpen, setSnackbarErrorOpen] = useState(false);
  const [productDetails, setProductDetails] = useState(null); // Add state for product details
  const [loading, setLoading] = useState(false); // Add loading state
  // INR-specific tax inputs
  const [hsnCode, setHsnCode] = useState('');
  const [fetchingTax, setFetchingTax] = useState(false);
  const [taxPreview, setTaxPreview] = useState(null); // store computed tax data for preview/meta
  const [taxError, setTaxError] = useState('');
  const [scanOpen, setScanOpen] = useState(false);

  const { orgId, setOrgId } = useContext(OrgContext); // Get orgId from OrgContext
  const { currency } = useContext(CurrencyContext);
  const { orgId: pathOrgId } = useParams(); // Get orgId from path
  const navigate = useNavigate();
  const { setIsLoading } = useLoading();

  useEffect(() => {
    if (!orgId && pathOrgId) {
      setOrgId(pathOrgId); // Set orgId from path if not already set
    }
  }, [orgId, pathOrgId, setOrgId]);

  useEffect(() => {
    if (!orgId) {
      navigate('/list-orgs'); // Redirect to list-orgs if orgId is not set
    }
  }, [orgId, navigate]);

  async function handleAddProduct() {
    if (!productName || !category || !unitOfMeasure || !company || !sku || !currentUnitCost || !currentUnitPrice) { // Remove status from required fields
      setError('Please fill in all required fields.');
      setSnackbarErrorOpen(true);
      return;
    }

    // If INR and HSN provided but no fetched taxes yet, try fetching before save
    if (currency === 'INR' && hsnCode && !taxPreview) {
      try {
        await handleFetchTaxes();
      } catch (e) {
        // Non-blocking; allow save even if tax fetch fails
      }
    }

    setLoading(true);
    setIsLoading(true);
    try {
      const payload = {
        org_id: parseInt(orgId),
        product_name: productName,
        category,
        unit_of_measure: unitOfMeasure,
        scanner_reference: scannerRef,
        sub_category: subCategory,
        type_of_inventory: typeOfInventory.toLowerCase(), // Convert to lowercase
        company,
        sku,
        current_unit_cost: parseFloat(currentUnitCost),
        current_unit_price: parseFloat(currentUnitPrice),
      };

      // Attach meta for INR with HSN and taxes
      if (currency === 'INR' && hsnCode) {
        payload.meta = {
          hsn_code: hsnCode,
          tax: taxPreview
            ? taxPreview
            : undefined,
        };
      }

      const response = await apiClient.post('/products', payload);
      setProductDetails(response.data); // Set product details
      setSnackbarOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Error adding product');
      setSnackbarErrorOpen(true);
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  }

  // Fetch taxes for INR using backend tax endpoint
  const handleFetchTaxes = async () => {
    if (!hsnCode) {
      setTaxError('Please enter HSN code');
      return;
    }
    setTaxError('');
    setFetchingTax(true);
    try {
      const { data } = await apiClient.get(`/tax/hsn/${encodeURIComponent(hsnCode)}`);
      // Compute final cumulative rate - choose the max rate option as default
      const final = data?.summary?.maxCumulativeRate ?? 0;
      const normalized = {
        source: 'fastgst',
        hsn: data?.hsn || hsnCode,
        gst: data?.gst || null,
        cess: data?.cess || null,
        options: data?.options || [],
        final_percentage: final,
        computed_at: new Date().toISOString(),
      };
      setTaxPreview(normalized);
    } catch (e) {
      setTaxError(e?.response?.data?.message || e.message || 'Failed to fetch tax');
    } finally {
      setFetchingTax(false);
    }
  };

  function handleAddAnotherProduct() {
    setProductDetails(null); // Reset product details
    setProductName('');
    setCategory('');
    setUnitOfMeasure('');
    setScannerRef('');
    setSubCategory('');
    setTypeOfInventory('permanent');
    setCompany('');
    setSku('');
    setCurrentUnitCost('');
    setCurrentUnitPrice('');
  }

  return (
    <>
    <Box sx={{ p: 2 }}>
      <BackButton />  {/* Add BackButton here */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : productDetails ? (
        <Box>
          <Typography variant="h5">Product Details</Typography>
          <Typography>Product Name: {productDetails.product_name}</Typography>
          <Typography>Category: {productDetails.category}</Typography>
          <Typography>Unit of Measure: {productDetails.unit_of_measure}</Typography>
          <Typography>Scanner Reference: {productDetails.scanner_reference}</Typography>
          <Typography>Sub Category: {productDetails.sub_category}</Typography>
          <Typography>Type of Inventory: {productDetails.type_of_inventory}</Typography>
          <Typography>Company: {productDetails.company}</Typography>
          <Typography>SKU: {productDetails.sku}</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddAnotherProduct}
            fullWidth
            sx={{ mt: 2 }}
          >
            Add Another Product
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => navigate(`/${orgId}/inventory/add`)}
            fullWidth
            sx={{ mt: 2 }}
          >
            Add Inventory
          </Button>
        </Box>
      ) : (
        <Box>
          <Typography variant="h5">Add Product to Org {orgId}</Typography>
          <form autoComplete="off">
            <TextField
              label="Product Name"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              fullWidth
              required
              sx={{ my: 1 }}
              autoComplete="off"
              inputProps={{
                autoComplete: 'off',
                autoCorrect: 'off',
                autoCapitalize: 'off',
                spellCheck: false
              }}
            />
            <TextField
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              fullWidth
              required
              sx={{ my: 1 }}
              autoComplete="off"
              inputProps={{
                autoComplete: 'off',
                autoCorrect: 'off',
                autoCapitalize: 'off',
                spellCheck: false
              }}
            />
            <TextField
              label="Unit of Measure"
              value={unitOfMeasure}
              onChange={(e) => setUnitOfMeasure(e.target.value)}
              fullWidth
              required
              sx={{ my: 1 }}
              autoComplete="off"
              inputProps={{
                autoComplete: 'off',
                autoCorrect: 'off',
                autoCapitalize: 'off',
                spellCheck: false
              }}
            />
            <TextField
              label="Scanner Reference"
              value={scannerRef}
              onChange={(e) => setScannerRef(e.target.value)}
              fullWidth
              sx={{ my: 1 }}
              autoComplete="off"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label="scan barcode" onClick={() => setScanOpen(true)} edge="end">
                      <PhotoCamera />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              inputProps={{
                autoComplete: 'off',
                autoCorrect: 'off',
                autoCapitalize: 'off',
                spellCheck: false
              }}
            />
            <TextField
              label="Sub Category"
              value={subCategory}
              onChange={(e) => setSubCategory(e.target.value)}
              fullWidth
              sx={{ my: 1 }}
              autoComplete="off"
              inputProps={{
                autoComplete: 'off',
                autoCorrect: 'off',
                autoCapitalize: 'off',
                spellCheck: false
              }}
            />
            <TextField
              label="Company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              fullWidth
              required
              sx={{ my: 1 }}
              autoComplete="off"
              inputProps={{
                autoComplete: 'off',
                autoCorrect: 'off',
                autoCapitalize: 'off',
                spellCheck: false
              }}
            />
            <TextField
              label="SKU"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              fullWidth
              required
              sx={{ my: 1 }}
              autoComplete="off"
              inputProps={{
                autoComplete: 'off',
                autoCorrect: 'off',
                autoCapitalize: 'off',
                spellCheck: false
              }}
            />
            <TextField
              label="Current Unit Cost"
              value={currentUnitCost}
              onChange={(e) => setCurrentUnitCost(e.target.value)}
              type="number"
              step="0.01"
              fullWidth
              required
              sx={{ my: 1 }}
              autoComplete="off"
              inputProps={{
                autoComplete: 'off',
                autoCorrect: 'off',
                autoCapitalize: 'off',
                spellCheck: false
              }}
            />
            <TextField
              label="Current Unit Price"
              value={currentUnitPrice}
              onChange={(e) => setCurrentUnitPrice(e.target.value)}
              type="number"
              step="0.01"
              fullWidth
              required
              sx={{ my: 1 }}
              autoComplete="off"
              inputProps={{
                autoComplete: 'off',
                autoCorrect: 'off',
                autoCapitalize: 'off',
                spellCheck: false
              }}
            />
            <FormControl fullWidth required sx={{ my: 1 }}>
              <InputLabel>Type of Inventory</InputLabel>
              <Select
                value={typeOfInventory}
                onChange={(e) => setTypeOfInventory(e.target.value)}
              >
                <MenuItem value="permanent">Permanent</MenuItem>
                <MenuItem value="perishing">Perishing</MenuItem>
              </Select>
            </FormControl>
            {currency === 'INR' && (
              <Box sx={{ my: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                <Typography variant="subtitle1">GST/Tax (India)</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                  <TextField
                    label="HSN Code"
                    value={hsnCode}
                    onChange={(e) => setHsnCode(e.target.value)}
                    fullWidth
                    autoComplete="off"
                  />
                  <Button variant="outlined" onClick={handleFetchTaxes} disabled={fetchingTax}>
                    {fetchingTax ? 'Fetching…' : 'Fetch taxes'}
                  </Button>
                </Box>
                {taxError && (
                  <Typography color="error" variant="caption">{taxError}</Typography>
                )}
                {taxPreview && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">Final cumulative tax: {taxPreview.final_percentage}%</Typography>
                    {taxPreview.gst?.type && (
                      <Typography variant="caption" color="text.secondary">
                        GST Type: {taxPreview.gst.type}{taxPreview.gst.conditionType ? ` (${taxPreview.gst.conditionType})` : ''}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}
            <CustomDatePicker
              label="Date"
              value={new Date()}
              onChange={() => {}}
              fullWidth
              required
              sx={{ my: 1 }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddProduct}
              fullWidth
            >
              Add Product
            </Button>
          </form>
        </Box>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message="Product added!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            color: '#ffffff',
            backgroundColor: 'primary.main',
            textAlign: 'center'
          }
        }}
      />
      <Snackbar
        open={snackbarErrorOpen}
        onClose={() => setSnackbarErrorOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={<span style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>{error}</span>}
        ContentProps={{
          sx: {
            color: '#ffffff',
            backgroundColor: 'error.main',
            textAlign: 'center'
          }
        }}
      />
  </Box>
  <CameraScanner
      open={scanOpen}
      onClose={() => setScanOpen(false)}
      onDetected={(code) => {
        setScannerRef(code);
        setScanOpen(false);
      }}
      title="Scan Scanner Reference"
    />
  </>
  );
}

export default AddProductPage;
